import React, { useState, FC } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Sidebar, Flowbite } from 'flowbite-react';
import { useEffect } from 'react';
import { useQuery } from 'react-query';
import {
  MarketingIcon,
  MarketingActive,
  MembersIcon,
  MembersActive,
  OffboardingIcon,
  OffboardingActive,
  OnboardingIcon,
  OnboardingActive,
  ReportsIcon,
  ReportsActive,
  PerformanceIcon,
  PerformanceActive,
  ArrowDown,
  ArrowUp,
  RmmAutomationIcon,
} from '../../components/icons';
import { cn } from '../utils/cn';
import { boolean } from '../utils/boolean';
import { usePermissions } from '../hooks/usePermissions';
import { roleManagementStrings } from '../pages/memberManagement/RoleManagement/strings';
import { printShopStrings } from '../pages/printShop/strings';
import { rmQueueStrings } from '../pages/printShop/RmQueue/strings';
import { mldStrings } from '../pages/mld/strings';
import { fetchBatchCounts } from '../pages/printShop/Batches/api';
import {
  isPrintShopNavItemActive,
  MAIL_ON_DEMAND_ONLY_PATHS,
  PRINT_SHOP_QUEUE_PATHS,
} from '../pages/printShop/queues';
import env from '../../environments';

function SidebarNav({ openMobileDevice, menuMobileClicked }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [activePath, setActivePath] = useState(location.pathname);
  const [windowSize, setWindowSize] = useState(window.innerWidth);
  const showMagazineRecipients = boolean(
    import.meta.env.VITE_MAGAZINE_RECIPIENTS_FEATURE,
  );
  const { canAccessCoachMaintenance, canAccessRMM, canAccessPerformance, canAccessBookManagement } = usePermissions();

  // Live open-batch count for the Batches nav item's badge. Same
  // count-query convention as the print-shop queue's tab counts
  // (`fetchTicketCounts`): no polling, refetches on the query's own normal
  // react-query cadence (mount/refetch triggers), not window focus.
  // Mail on Demand (its queue, Batches, Pricing) is hidden unless
  // VITE_ENABLE_MOD is on; hidden, the batch count is not fetched either.
  const modEnabled = env.ENABLE_MOD;
  const isPrintShopNavItemShown = (subItem: { route: string }) =>
    modEnabled || !MAIL_ON_DEMAND_ONLY_PATHS.includes(subItem.route);
  const { data: batchCounts } = useQuery(
    ['print-shop-batch-counts'],
    fetchBatchCounts,
    { refetchOnWindowFocus: false, enabled: modEnabled },
  );

  useEffect(() => {
    const handleResize = () => {
      setWindowSize(window.innerWidth);
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const isMobile = windowSize < 1024;

  useEffect(() => {
    setActivePath(location.pathname);
  }, [location]);

  const renderChevron = (open) =>
    open ? (
      <ArrowUp aria-hidden="true" className="w-5 h-5" />
    ) : (
      <ArrowDown aria-hidden="true" className="w-5 h-5" />
    );

  const handleItemClick = (path) => {
    navigate(path);
    if (isMobile) menuMobileClicked();
  };

  const iconHOC =
    (Icon: FC<React.SVGProps<SVGSVGElement>>, active: boolean) =>
    (props?: React.SVGProps<SVGSVGElement>) =>
      (
        <Icon
          {...props}
          className={`h-5 w-5 ${active ? 'text-black' : 'text-gray-400'}`}
          style={{ width: '20px', height: '20px' }}
        />
      );

  const chooseIcon = (
    defaultIcon: FC<React.SVGProps<SVGSVGElement>>,
    activeIcon: FC<React.SVGProps<SVGSVGElement>>,
    isActive: boolean,
  ) => {
    return isActive
      ? iconHOC(activeIcon, isActive)
      : iconHOC(defaultIcon, isActive);
  };

  const onboardingPaths = [
    '/coach-maintenance',
    '/sales-report',
    '/team-meetings',
    '/team-phone-calls',
  ];
  const marketingPaths = ['/offers'];
  const isOnboardingActive = onboardingPaths.some((path) =>
    activePath.startsWith(path),
  );
  const isMarketingActive = marketingPaths.some((path) =>
    activePath.startsWith(path),
  );
  const isReportsActive = [
    '/tripwire-report',
    '/rm-magazine-report',
    '/magazine-recipient',
  ].some((path) => activePath.startsWith(path));
  const isOffboardingActive = activePath.includes('/offboarding');
  // Member List Deals (VITE_ENABLE_MLD only): `/mld` and its detail pages.
  // Matched from the start of the path, so an unrelated path that merely
  // contains `/mld` (or starts with `/mldx`) is not an MLD page.
  const isMldActive =
    env.ENABLE_MLD &&
    (activePath === '/mld' || activePath.startsWith('/mld/'));
  const isMembersActive = activePath.includes('/member-password-change') || activePath.includes('/customer-information') || activePath.includes('/member-activity') || activePath.includes('/account-managers') || activePath.includes('/book-management') || activePath.includes('/asset-management') || activePath.includes('/book-drafts') || isMldActive;
  const isRoleManagementActive = activePath.includes('/role-management');
  const isRmmActive = activePath.includes('/referral-marketing');
  const isPerformanceActive = activePath.includes('/performance');

  const renderOnboardingItems = () => {
    return onboardingPaths
      .map((path, index) => {
        if (path === '/coach-maintenance' && !canAccessCoachMaintenance()) {
          return null;
        }

        return (
          <Sidebar.Item
            key={index}
            onClick={() => handleItemClick(path)}
            className={`hover:bg-[#EDFAFC] ${
              path === activePath || activePath.startsWith(path)
                ? 'bg-sidebar-item-selected relative sidebar-item-active'
                : ''
            } sidebar-text-style pl-7 py-[9.5px]`}
          >
            {
              [
                'Coach Maintenance',
                'Sales Report',
                'Team Meetings',
                'Team Phone Calls',
              ][index]
            }
          </Sidebar.Item>
        );
      })
      .filter(Boolean);
  };

  return (
    <div className="fixed top-14 left-0 max-[1024px]:w-full max-[1024px]:px-1 items-center">
      <Flowbite>
        <Sidebar
          aria-label="Sidebar with dynamic navigation items"
          className={cn('border-gray-200 bg-white overflow-x-visible', {
            'h-fit border w-[calc(100%-8px)] shadow-box rounded-b-lg ml-1':
              isMobile,
            'border-r border-t h-screen w-[272px]': !isMobile,
            hidden: !openMobileDevice && isMobile,
          })}
        >
          <Sidebar.Items className="border-none">
            <Sidebar.ItemGroup className="border-none mt-0">
              <Sidebar.Collapse
                icon={chooseIcon(
                  OnboardingIcon,
                  OnboardingActive,
                  isOnboardingActive,
                )}
                label="Onboarding"
                className="hover:bg-[#EDFAFC] sidebar-text-style py-[9.5px]"
                renderChevronIcon={(theme, open) => renderChevron(open)}
              >
                {renderOnboardingItems()}
              </Sidebar.Collapse>
              <Sidebar.Collapse
                icon={chooseIcon(
                  MarketingIcon,
                  MarketingActive,
                  isMarketingActive,
                )}
                label="Marketing"
                className="hover:bg-[#EDFAFC] sidebar-text-style py-[9.5px]"
                renderChevronIcon={(theme, open) => renderChevron(open)}
              >
                <Sidebar.Item
                  onClick={() => handleItemClick('/offers')}
                  className={`hover:bg-[#EDFAFC] ${
                    isMarketingActive
                      ? 'bg-sidebar-item-selected relative sidebar-item-active'
                      : ''
                  } sidebar-text-style pl-7 py-[9.5px]`}
                >
                  Offers
                </Sidebar.Item>
              </Sidebar.Collapse>
              <Sidebar.Collapse
                icon={chooseIcon(ReportsIcon, ReportsActive, isReportsActive)}
                label="Reports"
                className="hover:bg-[#EDFAFC] sidebar-text-style py-[9.5px]"
                renderChevronIcon={(theme, open) => renderChevron(open)}
              >
                {[
                  { route: '/tripwire-report', title: 'Tripwire Sales Report' },
                  { route: '/rm-magazine-report', title: 'RM Magazine' },
                  showMagazineRecipients
                    ? {
                        route: '/magazine-recipient',
                        title: 'Magazine Recipients',
                      }
                    : null,
                ]
                  .filter(
                    (subItem): subItem is { route: string; title: string } =>
                      subItem !== null,
                  )
                  .map((subItem) => (
                    <Sidebar.Item
                      key={subItem.route}
                      onClick={() => handleItemClick(subItem.route)}
                      className={`hover:bg-[#EDFAFC] ${
                        subItem.route === activePath ||
                        activePath.startsWith(subItem.route)
                          ? 'bg-sidebar-item-selected relative sidebar-item-active'
                          : ''
                      } sidebar-text-style pl-7 py-[9.5px]`}
                    >
                      {subItem.title}
                    </Sidebar.Item>
                  ))}
              </Sidebar.Collapse>
              {canAccessPerformance() && (
                <Sidebar.Collapse
                  icon={chooseIcon(
                    PerformanceIcon,
                    PerformanceActive,
                    isPerformanceActive,
                  )}
                  label="Performance"
                  className="hover:bg-[#EDFAFC] sidebar-text-style py-[9.5px]"
                  renderChevronIcon={(theme, open) => renderChevron(open)}
                >
                  <Sidebar.Item
                    onClick={() => handleItemClick('/performance')}
                    className={`hover:bg-[#EDFAFC] ${
                      '/performance' === activePath ||
                      activePath.startsWith('/performance')
                        ? 'bg-sidebar-item-selected relative sidebar-item-active'
                        : ''
                    } sidebar-text-style pl-7 py-[9.5px]`}
                  >
                    Dashboard
                  </Sidebar.Item>
                </Sidebar.Collapse>
              )}
              {canAccessRMM() && (
                <Sidebar.Item
                  icon={() => <RmmAutomationIcon active={isRmmActive} />}
                  onClick={() => handleItemClick('/referral-marketing')}
                  className={`hover:bg-[#EDFAFC] ${
                    '/referral-marketing' === activePath
                      ? 'bg-sidebar-item-selected relative sidebar-item-active'
                      : ''
                  } sidebar-text-style py-[9.5px]`}
                >
                  RMM Automation
                </Sidebar.Item>
              )}
              <Sidebar.Item
                icon={chooseIcon(
                  OffboardingIcon,
                  OffboardingActive,
                  isOffboardingActive,
                )}
                onClick={() => handleItemClick('/offboarding')}
                className={`hover:bg-[#EDFAFC] ${
                  '/offboarding' === activePath
                    ? 'bg-sidebar-item-selected relative sidebar-item-active'
                    : ''
                } sidebar-text-style py-[9.5px]`}
              >
                Offboarding
              </Sidebar.Item>
              <Sidebar.Collapse
                icon={chooseIcon(MembersIcon, MembersActive, isMembersActive)}
                label="Members"
                // Opened while one of its pages is showing, so the selected
                // item (e.g. Member List Deals on /mld and /mld/:id) is
                // visible on a fresh load. The operator can still close it.
                // Ships with Member List Deals: with VITE_ENABLE_MLD off the
                // group keeps its previous behaviour (closed until clicked).
                open={env.ENABLE_MLD && isMembersActive}
                className="hover:bg-[#EDFAFC] sidebar-text-style py-[9.5px]"
                renderChevronIcon={(theme, open) => renderChevron(open)}
              >
                <Sidebar.Item
                  onClick={() => handleItemClick('/customer-information')}
                  className={`hover:bg-[#EDFAFC] ${
                    activePath.startsWith('/customer-information')
                      ? 'bg-sidebar-item-selected relative sidebar-item-active'
                      : ''
                  } sidebar-text-style pl-7 py-[9.5px]`}
                >
                  Customer Information
                </Sidebar.Item>
                <Sidebar.Item
                  onClick={() => handleItemClick('/member-activity')}
                  className={`hover:bg-[#EDFAFC] ${
                    activePath.startsWith('/member-activity')
                      ? 'bg-sidebar-item-selected relative sidebar-item-active'
                      : ''
                  } sidebar-text-style pl-7 py-[9.5px]`}
                >
                  Member Activity
                </Sidebar.Item>
                <Sidebar.Item
                  onClick={() => handleItemClick('/account-managers')}
                  className={`hover:bg-[#EDFAFC] ${
                    activePath.startsWith('/account-managers')
                      ? 'bg-sidebar-item-selected relative sidebar-item-active'
                      : ''
                  } sidebar-text-style pl-7 py-[9.5px]`}
                >
                  Account Managers
                </Sidebar.Item>
                {canAccessBookManagement() && (
                  <Sidebar.Item
                    onClick={() => handleItemClick('/book-management')}
                    className={`hover:bg-[#EDFAFC] ${
                      activePath.startsWith('/book-management')
                        ? 'bg-sidebar-item-selected relative sidebar-item-active'
                        : ''
                    } sidebar-text-style pl-7 py-[9.5px]`}
                  >
                    PDF Books
                  </Sidebar.Item>
                )}
                {canAccessBookManagement() && (
                  <Sidebar.Item
                    onClick={() => handleItemClick('/asset-management')}
                    className={`hover:bg-[#EDFAFC] ${
                      activePath.startsWith('/asset-management')
                        ? 'bg-sidebar-item-selected relative sidebar-item-active'
                        : ''
                    } sidebar-text-style pl-7 py-[9.5px]`}
                  >
                    Asset Management
                  </Sidebar.Item>
                )}
                {canAccessBookManagement() && (
                  <Sidebar.Item
                    onClick={() => handleItemClick('/book-drafts')}
                    className={`hover:bg-[#EDFAFC] ${
                      activePath.startsWith('/book-drafts')
                        ? 'bg-sidebar-item-selected relative sidebar-item-active'
                        : ''
                    } sidebar-text-style pl-7 py-[9.5px]`}
                  >
                    Book Draft Switcher
                  </Sidebar.Item>
                )}
                {env.ENABLE_MLD && (
                  <Sidebar.Item
                    onClick={() => handleItemClick('/mld')}
                    className={`hover:bg-[#EDFAFC] ${
                      isMldActive
                        ? 'bg-sidebar-item-selected relative sidebar-item-active'
                        : ''
                    } sidebar-text-style pl-7 py-[9.5px]`}
                  >
                    {mldStrings.sidebarLabel}
                  </Sidebar.Item>
                )}
                <Sidebar.Item
                  onClick={() => handleItemClick('/role-management')}
                  className={`hover:bg-[#EDFAFC] ${
                    '/role-management' === activePath
                      ? 'bg-sidebar-item-selected relative sidebar-item-active'
                      : ''
                  } sidebar-text-style pl-7 py-[9.5px]`}
                >
                  {roleManagementStrings.sidebarLabel}
                </Sidebar.Item>
              </Sidebar.Collapse>
              <Sidebar.Collapse
                icon={chooseIcon(
                  ReportsIcon,
                  ReportsActive,
                  activePath.startsWith('/print-shop'),
                )}
                label={printShopStrings.sidebar.groupLabel}
                className="hover:bg-[#EDFAFC] sidebar-text-style py-[9.5px]"
                renderChevronIcon={(theme, open) => renderChevron(open)}
              >
                {(
                  [
                    // One entry per queue, then the tools (abo-print-shop-
                    // menu-split). The order is a product decision.
                    {
                      route: PRINT_SHOP_QUEUE_PATHS.mod,
                      title: printShopStrings.sidebar.modQueue,
                    },
                    {
                      route: PRINT_SHOP_QUEUE_PATHS.book,
                      title: printShopStrings.sidebar.booksQueue,
                    },
                    {
                      route: PRINT_SHOP_QUEUE_PATHS.rm,
                      title: rmQueueStrings.sidebar.magazineQueue,
                    },
                    {
                      route: '/print-shop/batches',
                      title: printShopStrings.sidebar.batches,
                      badgeCount: batchCounts?.open,
                    },
                    {
                      route: '/print-shop/pricing-config',
                      title: printShopStrings.sidebar.pricingConfig,
                    },
                  ] as { route: string; title: string; badgeCount?: number }[]
                ).filter(isPrintShopNavItemShown).map((subItem) => (
                  <Sidebar.Item
                    key={subItem.route}
                    data-testid={`print-shop-nav-${subItem.route}`}
                    onClick={() => handleItemClick(subItem.route)}
                    className={`hover:bg-[#EDFAFC] ${
                      isPrintShopNavItemActive(subItem.route, activePath)
                        ? 'bg-sidebar-item-selected relative sidebar-item-active'
                        : ''
                    } sidebar-text-style pl-7 py-[9.5px]`}
                  >
                    <span className="flex w-full items-center justify-between gap-2">
                      {subItem.title}
                      {subItem.badgeCount !== undefined && (
                        <span
                          data-testid="batches-open-count-badge"
                          className="rounded-full bg-neutral-100 px-2 py-0.5 text-xs text-neutral-500"
                        >
                          {subItem.badgeCount}
                        </span>
                      )}
                    </span>
                  </Sidebar.Item>
                ))}
              </Sidebar.Collapse>
            </Sidebar.ItemGroup>
          </Sidebar.Items>
        </Sidebar>
      </Flowbite>
    </div>
  );
}

export default SidebarNav;
